import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${window.location.origin}/api/budget`;
const split = (v) => v.split(/[,;]/).map((x) => x.trim()).filter(Boolean);

/** BOD-1 — Request Approval draft (Ask: Basis of Design / Approved Equal / Manufacturer Approval). Nothing goes out until the user presses Send. */
export const ApprovedEqualDialog = ({ row, onClose }) => {
  const [draft, setDraft] = useState(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [ask, setAsk] = useState("");

  const build = (askKey) => {
    axios.get(`${API}/budgets/${row.budget_id}/approved-equal/draft`, { params: { scope_code: row.scope_code, ask: askKey || "", recipient: row.recipients?.[0] || "" } })
      .then(({ data }) => { setDraft(data); setAsk(data.ask); setTo((row.recipients?.length ? row.recipients : data.to).join(", ")); setCc(""); setSubject(data.subject); setBody(data.body); })
      .catch((e) => { toast.error(e.response?.data?.detail || "Could not build the draft"); onClose(); });
  };
  useEffect(() => {
    if (!row) return;
    setDraft(null);
    build("");                                   // "" = the user's remembered Ask
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/budgets/${row.budget_id}/approved-equal/send`,
        { scope_code: row.scope_code, to: split(to), cc: split(cc), subject, body, ask, greeting_name: draft.greeting_name || "" });
      toast.success(`Sent to ${data.sent.join(", ")}`);
      onClose(true);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="approved-equal-dialog">
        <DialogHeader>
          <DialogTitle>Request Approval</DialogTitle>
          <DialogDescription className="text-xs">Review and edit — nothing is sent until you press Send.</DialogDescription>
        </DialogHeader>
        {!draft ? <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-3">
            <div className="text-xs text-slate-500" data-testid="ae-sender">
              From <b className="text-slate-700 dark:text-slate-200">{draft.from_name}</b> &lt;{draft.from_email}&gt; · Reply-To {draft.reply_to}
            </div>
            {split(to).length > 1 && (
              <div className="text-xs text-sky-700 dark:text-sky-400" data-testid="ae-separate">
                {split(to).length} separate emails — each recipient gets their own, greeted by their first name.
              </div>
            )}
            {draft.scopes.length > 1 && (
              <div className="text-xs text-slate-500" data-testid="ae-bundled">Bundled: {draft.scopes.length} scopes on this budget</div>
            )}
            {draft.missing_manufacturer.length > 0 && (
              <div className="text-xs text-amber-700" data-testid="ae-missing-manufacturer">
                No manufacturer on {draft.missing_manufacturer.join(", ")} — add one in Manufacturers or edit the text.
              </div>
            )}
            <div>
              <Label className="text-xs">Ask</Label>
              <select value={ask} onChange={(e) => build(e.target.value)} data-testid="ae-ask"
                      className="mt-1 h-8 w-full rounded-sm border border-border bg-card px-2 text-sm text-foreground">
                {(draft.asks || []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              <div className="mt-1 text-[11px] text-muted-foreground" data-testid="ae-ask-sentence">{draft.ask_sentence}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} className="h-8 text-sm" data-testid="ae-to" /></div>
              <div><Label className="text-xs">Cc</Label><Input value={cc} onChange={(e) => setCc(e.target.value)} className="h-8 text-sm" data-testid="ae-cc" /></div>
            </div>
            <div><Label className="text-xs">Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-8 text-sm" data-testid="ae-subject" /></div>
            <div><Label className="text-xs">Message</Label><Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="text-sm" data-testid="ae-body" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()} data-testid="ae-cancel">Cancel</Button>
          <Button variant="data" disabled={!draft || sending || !split(to).length} onClick={send} className="gap-1.5" data-testid="ae-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
